import { AxiosError } from 'axios';
import axios from 'services/axiosClient';
import {
  CreateDesignation,
  CreateDesignationGroup,
  DeleteDesignationGroup,
  Designation,
  DesignationGroup,
  IGetDesignationGroups,
  IGetDesignations,
  ToggleDesignationStatus,
  UpdateDesignation
} from './types';

export const getDesignationsService = async ({
  organizationId,
  campaignId,
  isActiveFilter,
  augmentWithGroups = false
}: IGetDesignations): Promise<Designation[]> => {
  try {
    const searchParams = new URLSearchParams();

    searchParams.set('organizationId', organizationId);
    if (campaignId) {
      searchParams.set('campaignId', campaignId);
    }
    // isActiveFilter defaults to true in backend
    if (isActiveFilter !== undefined) {
      searchParams.set('isActiveFilter', `${isActiveFilter}`);
    }

    const designationResponse = await axios.get(
      `/designations?${searchParams.toString()}`
    );

    const designationData: Designation[] = designationResponse.data.map(
      (d: Designation) => ({
        ...d,
        groups: [] as DesignationGroup[]
      })
    );

    if (augmentWithGroups) {
      // augment returned objects with group data from a second API call
      const { data: groupData } = await axios.get(
        `/designations/groups?${searchParams.toString()}`
      );

      const designationsById = (designationData as Designation[]).reduce<
        Record<string, Designation>
      >((acc, val) => {
        acc[val.id] = val;
        return acc;
      }, {});

      groupData.forEach((group: DesignationGroup) => {
        group.designations.forEach((d) => {
          const designation = designationsById[d.id];
          if (designation) {
            designation.groups.push(group);
          }
        });
      });
    }

    return designationData;
  } catch (e) {
    throw Error(
      (e as AxiosError).response.data.message || 'Error retrieving designations'
    );
  }
};

export const createDesignationService = async ({
  organizationId,
  designationFormData
}: CreateDesignation): Promise<Designation> => {
  try {
    const { data } = await axios.post(
      `/designations?organizationId=${organizationId}`,
      designationFormData
    );
    return data.result;
  } catch (e) {
    throw Error(
      (e as AxiosError).response.data.message || 'Error creating designation'
    );
  }
};

export const updateDesignationService = async ({
  organizationId,
  designationFormData,
  designationId
}: UpdateDesignation): Promise<Designation> => {
  try {
    const { data } = await axios.put(
      `/designations?organizationId=${organizationId}&designationId=${designationId}`,
      designationFormData
    );
    return data;
  } catch (e) {
    throw Error(
      (e as AxiosError).response.data.message || 'Error updating designation'
    );
  }
};

export const getDesignationGroupsService = async ({
  organizationId,
  campaignId
}: IGetDesignationGroups): Promise<DesignationGroup[]> => {
  try {
    const searchParams = new URLSearchParams();

    searchParams.set('organizationId', organizationId);
    if (campaignId) {
      searchParams.set('campaignId', campaignId);
    }

    const { data } = await axios.get(
      `/designations/groups?${searchParams.toString()}`
    );
    return data;
  } catch (e) {
    throw Error(
      (e as AxiosError).response.data.message ||
        'Error retrieving designation groups'
    );
  }
};

export const createDesignationGroupService = async ({
  organizationId,
  groupName
}: CreateDesignationGroup): Promise<DesignationGroup> => {
  try {
    const { data } = await axios.post(
      `/designations/groups?organizationId=${organizationId}`,
      { groupName }
    );
    return data;
  } catch (e) {
    throw Error(
      (e as AxiosError).response.data.message ||
        'Error creating designation group'
    );
  }
};

export const deleteDesignationGroupByIdService = async ({
  organizationId,
  designationGroupId
}: DeleteDesignationGroup) => {
  try {
    await axios.delete(
      `/designations/groups/${designationGroupId}?organizationId=${organizationId}`
    );
  } catch (e) {
    throw Error(
      (e as AxiosError).response.data.message ||
        'Error deleting designation group'
    );
  }
};

export const toggleDesignationStatusService = async ({
  organizationId,
  designationId,
  isActive
}: ToggleDesignationStatus) => {
  try {
    await axios.put(
      `/designations/${designationId}/toggleStatus?organizationId=${organizationId}`,
      { isActive }
    );
  } catch (e) {
    throw Error(
      (e as AxiosError).response.data.message ||
        'Error toggling designation status'
    );
  }
};
